import React from "react";
import { isSafeHttpUrl } from "../../utils/urlSafety";

export type LegalLinkVariant = "standalone" | "consent";

export interface LegalLinkProps {
  /** Resolved URL, or `null`/`undefined` while loading or on a genuine failure. */
  url: string | null | undefined;
  /** Visible link text, e.g. "Regulamin" / "Polityka prywatności". */
  label: string;
  /**
   * True while `GET /brands/detect` is still in flight (H8) — renders a
   * calm, neutral placeholder and NEVER the "link niedostępny" copy, which
   * is reserved for a genuine post-fetch failure (unresolved brand or
   * unsafe URL).
   */
  isLoading: boolean;
  /** data-testid for the "link niedostępny" placeholder (post-fetch failure only). */
  unavailableTestId: string;
  /**
   * data-testid for the loading placeholder. Defaults to `unavailableTestId`
   * with its `-unavailable` suffix swapped for `-loading`.
   */
  loadingTestId?: string;
  /** className applied to the `<a>` when the link renders. */
  className?: string;
  /**
   * `"standalone"` (Footer, CookieBanner running text — plain label) or
   * `"consent"` (Register's consent-checkbox label — bold label + a
   * sr-only "opens in new tab" hint on the link, matching
   * `ReacceptanceModal`'s pattern). Defaults to `"standalone"`.
   */
  variant?: LegalLinkVariant;
}

/**
 * Renders a legal document link (Regulamin/Polityka prywatności) that is
 * safe against `null`/unsafe URLs — extracted from 5 duplicated call sites
 * (spec 2026-07-06-per-brand-legal-content, H9): `Register.tsx` (×2),
 * `Footer.tsx` (×2), `CookieBanner.tsx` (×1).
 *
 * Three states, ONE consistent visual treatment across every call site (H9 —
 * before this component, the same "unavailable" state was rendered as
 * discreet gray text in Footer, unstyled in CookieBanner, and alarming red
 * in Register):
 * - loading: calm/neutral label, no link, no "niedostępny" copy (H8).
 * - safe URL: a real `<a rel="noopener noreferrer">`.
 * - genuine failure (resolved but missing/unsafe URL): the single
 *   "link niedostępny" error affordance.
 *
 * Wrapped in `aria-live="polite"` so screen-reader users hear the
 * loading → link (or loading → unavailable) transition (M14).
 */
export const LegalLink: React.FC<LegalLinkProps> = ({
  url,
  label,
  isLoading,
  unavailableTestId,
  loadingTestId,
  className,
  variant = "standalone",
}) => {
  const resolvedLoadingTestId =
    loadingTestId ?? unavailableTestId.replace(/-unavailable$/, "-loading");
  const boldLabel = variant === "consent";

  let body: React.ReactNode;

  if (isLoading) {
    // H8: loading is a distinct, calm state — never the "link niedostępny"
    // copy, which is reserved for a genuine post-fetch failure below.
    body = (
      <span
        className={boldLabel ? "font-medium text-gray-500" : "text-gray-400"}
        data-testid={resolvedLoadingTestId}
      >
        {label}
      </span>
    );
  } else if (isSafeHttpUrl(url)) {
    body = (
      <a
        href={url as string}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {label}
        {variant === "consent" && (
          <span className="sr-only"> (otwiera się w nowej karcie)</span>
        )}
      </a>
    );
  } else {
    // Genuine failure — brand detection settled but the URL is still
    // missing/unsafe. The only place the "link niedostępny" copy is shown.
    body = (
      <span className={boldLabel ? "font-medium" : undefined}>
        {label}{" "}
        <span
          className="text-red-600 text-xs font-normal"
          data-testid={unavailableTestId}
        >
          (Link niedostępny — skontaktuj się z pomocą.)
        </span>
      </span>
    );
  }

  return <span aria-live="polite">{body}</span>;
};

export default LegalLink;
